import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ScrollText, Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Er ging iets mis. Probeer het opnieuw.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("De wachtwoorden komen niet overeen");
      return;
    }
    setLoading(true);
    try {
      await api.auth.resetPassword({ token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--paper)] px-5 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <img src="/logo.png" alt="Pamaverliefd logo" className="h-12 w-12 rounded-xl object-cover shadow-sm" />
          <div>
            <h1 className="font-serif text-3xl font-bold text-[var(--ink)]">Pamaverliefd</h1>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--ink-3)]">AI Transcriptie</p>
          </div>
        </div>

        {done ? (
          <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface)] p-6 text-center shadow-sm" data-testid="reset-success">
            <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-green-600" />
            <h2 className="mb-2 font-serif text-xl font-semibold text-[var(--ink)]">Wachtwoord gewijzigd</h2>
            <p className="text-sm text-[var(--ink-2)]">Je kunt nu inloggen met je nieuwe wachtwoord. Je wordt doorgestuurd…</p>
          </div>
        ) : !token ? (
          <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface)] p-6 text-center shadow-sm" data-testid="reset-no-token">
            <h2 className="mb-2 font-serif text-xl font-semibold text-[var(--ink)]">Ongeldige link</h2>
            <p className="text-sm text-[var(--ink-2)]">Deze herstel-link is onvolledig. Vraag een nieuwe aan.</p>
            <Link to="/forgot-password" className="mt-5 inline-block font-semibold text-[var(--brand)] hover:underline" data-testid="reset-to-forgot">Nieuwe link aanvragen</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface)] p-6 shadow-sm" data-testid="reset-form">
            <h2 className="mb-1 font-serif text-xl font-semibold text-[var(--ink)]">Nieuw wachtwoord</h2>
            <p className="mb-5 text-sm text-[var(--ink-3)]">Kies een nieuw wachtwoord voor je account.</p>

            {error && (
              <div className="mb-4 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="reset-error">{error}</div>
            )}

            <label className="mb-3 block">
              <span className="mb-1 block font-mono text-[11px] uppercase tracking-wider text-[var(--ink-3)]">Nieuw wachtwoord (min. 6 tekens)</span>
              <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input"
                className="h-11 w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--paper)] px-3 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]" />
            </label>
            <label className="mb-5 block">
              <span className="mb-1 block font-mono text-[11px] uppercase tracking-wider text-[var(--ink-3)]">Herhaal wachtwoord</span>
              <input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input"
                className="h-11 w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--paper)] px-3 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]" />
            </label>
            <button type="submit" disabled={loading} data-testid="reset-submit-button"
              className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-[var(--brand)] text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
              Wachtwoord opslaan
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
